// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch } from '../../../../store';
import { rejectCandidate } from '../../../../store/interviewHostSlice';
import styles from './candidates.module.scss';

interface RejectCandidateModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  jobId: string;
}

const RejectCandidateModal: React.FC<RejectCandidateModalProps> = ({
  open,
  onClose,
  candidateId,
  candidateName,
  jobId
}) => {
  const dispatch = useAppDispatch();
  const [notes, setNotes] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const handleReject = async (): Promise<void> => {
    setRejecting(true);
    try {
      await dispatch(rejectCandidate({ candidateId, jobId, notes })).unwrap();
      onClose();
      setNotes(''); // Reset notes after successful rejection
    } catch (error) {
      console.error('Failed to reject candidate:', error);
    } finally {
      setRejecting(false);
    }
  };

  const handleClose = (): void => {
    if (!rejecting) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Reject Candidate</h2>
          <button className={styles.modalClose} onClick={handleClose} disabled={rejecting}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.confirmText}>
            Are you sure you want to reject <strong>{candidateName}</strong>?
          </p>
          <label className={styles.formLabel}>
            Notes (Optional)
            <textarea
              className={styles.formTextarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any notes about why this candidate was rejected..."
              disabled={rejecting}
            />
          </label>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={handleClose} disabled={rejecting}>
            Cancel
          </button>
          <button className={styles.btnReject} onClick={handleReject} disabled={rejecting}>
            {rejecting ? 'Rejecting...' : 'Reject Candidate'}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default RejectCandidateModal;
